import React, { useContext, useEffect, useRef, useState } from 'react'
import { observer } from 'mobx-react-lite'
import { Form, Formik, FormikHelpers } from 'formik'
import * as Yup from 'yup'
import { Button, Message } from 'semantic-ui-react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCheckCircle } from '@fortawesome/free-solid-svg-icons/faCheckCircle'
import { useMsal } from '@azure/msal-react'
import { InteractionStatus } from '@azure/msal-browser'

import FWOTextInput from '../form/FWOTextInput'
import Conditional from '../common/Conditional'
import FWOCustomerTypes from '../helpers/FWOCustomerTypes'
import { UserProfile } from '../../models/Profile'
import ProfileService from '../../services/ProfileService'
import FWOLocation from '../helpers/FWOLocation'
import FWOIndustrySelector from '../helpers/FWOIndustrySelector'
import {
  ENQUIRIES_CONFIRMATION_PRIVACY_URL,
  TERMS_AND_CONDITIONS,
} from '../../public/external-links'

import { PATHS } from '../Routes'
import FormErrorDisplay from '../common/FormErrorMessages'
import useErrorHandler from '../../hooks/useErrorHandler'
import ErrorFallback from '../common/ErrorFallback'
import { ErrorBoundary } from 'react-error-boundary'
import UserRegistrationService from '../../services/UserRegistrationService'
import axios from 'axios'
import { LoaderContext } from '../context/FormLoader'
import { FormRegex } from '../../models/Common'
import { buildSelectItem } from '../../utilities/commonFunctions'
import { IProfileSelectItems } from '../../models/ProfileSelectItems'

const Register = observer(() => {
  const loader = useContext(LoaderContext)
  const [isSaved, setSaved] = useState(false)
  const { createUserProfile } = ProfileService
  const { getRegisterUserViewModel } = UserRegistrationService
  const [userProfile, setUserProfile] = useState<UserProfile>(new UserProfile())
  const { instance, inProgress } = useMsal()
  const activeAccount = instance.getActiveAccount()
  const mountedRef = useRef(false)
  const [userErrors, , handleErrors, , clearErrors] = useErrorHandler()

  const selectItems: IProfileSelectItems = {
    businessDescriptions: [],
    yesNoUnsure: [],
    awards: [],
    titles: [],
    genders: [],
    customerTypes: [],
    regions: [],
    awardIndustries: [],
    awardSubIndustries: [],
  }
  const [options, setProfileSelectItems] =
    useState<IProfileSelectItems>(selectItems)

  const validateSchema = Yup.object({
    firstName: Yup.string().required('First name is required'),
    lastName: Yup.string().required('Last name is required'),
    customerTypeId: Yup.string().required('Customer Type is required'),
    industryId: Yup.string().required('Business Industry is required'),
    subIndustryId: Yup.string().required('Business Sub-Industry is required'),
    businessLocationId: Yup.string().required('Business Location is required'),
    phone: Yup.string()
      .required('This field cannot be empty, it is required.')
      .typeError('This field cannot be empty, it is required.')
      .matches(FormRegex.Phone, 'Please enter a valid phone number')
      .min(6, 'Requires a minimum of 6 characters')
      .max(20, 'Must be exactly 20 or less characters')
      .nullable(true),
  })

  const handleFormSubmit = async (
    values: UserProfile,
    actions: FormikHelpers<UserProfile>
  ) => {
    clearErrors()
    setSaved(false)
    createUserProfile(values)
      .then(() => {
        setSaved(true)
        actions.setSubmitting(false)
      })
      .catch((error) => {
        if (error) {
          handleErrors(error?.errors)
        }
      })
      .finally(() => {
        actions.setSubmitting(false)
      })
  }

  useEffect(() => {
    mountedRef.current = true
    const source = axios.CancelToken.source()
    if (
      mountedRef.current &&
      activeAccount &&
      inProgress === InteractionStatus.None
    ) {
      loader.up()

      getRegisterUserViewModel(source)
        .then((response) => response.result)
        .then((result) => {
          setUserProfile({
            ...userProfile,
            ...result,
            emailAddress: activeAccount.idTokenClaims.emails[0],
          })

          selectItems.customerTypes = buildSelectItem(
            result.customerTypes,
            true
          )
          selectItems.regions = buildSelectItem(result.regions, true)
          selectItems.awardIndustries = buildSelectItem(
            result.awardIndustries,
            true
          )
          selectItems.awardSubIndustries = buildSelectItem(
            result.awardSubIndustries,
            false
          )

          setProfileSelectItems(selectItems)
        })
        .catch(() => {
          setUserProfile({
            ...userProfile,
            emailAddress: activeAccount.idTokenClaims.emails[0],
          })
        })
        .finally(() => {
          loader.down()
        })
    }

    return () => {
      mountedRef.current = false
    }
  }, [inProgress])

  return (
    <>
      <ErrorBoundary FallbackComponent={ErrorFallback}>
        <Formik
          validationSchema={validateSchema}
          enableReinitialize
          initialValues={userProfile}
          onSubmit={(values, actions) => {
            actions.setSubmitting(true)
            handleFormSubmit(values, actions)
          }}
        >
          {({ isValid, isSubmitting }) => (
            <Form className="ui form osp-variant large" autoComplete="off">
              <div className="grid grid-row">
                <div className="grid-col grid-col.desk-3">
                  <h1>Register for My account</h1>
                  <p>
                    Fill in the registration form below to get personalised
                    workplace law information and help.
                  </p>
                  <p>All fields are required</p>

                  <FWOTextInput
                    type="email"
                    placeholder="Email"
                    label="Email address"
                    ariaLabel="Email address"
                    name="emailAddress"
                    required={true}
                    showreadonly={true}
                  />
                  <FWOTextInput
                    type="text"
                    placeholder="First name"
                    label="First name"
                    ariaLabel="First name"
                    name="firstName"
                    required={true}
                    aria-label="First name"
                  />
                  <FWOTextInput
                    type="text"
                    placeholder="Last name"
                    label="Last name"
                    ariaLabel="Last name"
                    name="lastName"
                    required={true}
                    aria-label="Last name"
                  />

                  <FWOCustomerTypes
                    label="Customer type"
                    placeholder="Customer type"
                    name="customerTypeId"
                    required={false}
                    options={options.customerTypes}
                  />

                  <FWOIndustrySelector
                    industryFieldId="industryId"
                    industryLabel="Business Industry"
                    subIndustryFieldId="subIndustryId"
                    subIndustryLabel="Sub-Industry"
                    industryOptions={options.awardIndustries}
                    subIndustryOptions={options.awardSubIndustries}
                  />

                  <FWOLocation
                    label="Business location"
                    placeholder="Business location"
                    name="businessLocationId"
                    required={false}
                    options={options.regions}
                  />
                  <FWOTextInput
                    type="text"
                    label="Phone including area code or mobile number"
                    ariaLabel="Phone including area code or mobile number"
                    name="phone"
                    required={true}
                    showreadonly={false}
                    tooltip="Phone including area code or mobile number Note: international numbers should commence with 0011 or *. This is the phone number we use to contact you and to verify your identity when you call us. If you change this phone number, it will also reset the phone number you use to verify your account by SMS or phone call when you log in. The next time you log in, you'll be asked to set up a phone number to receive the SMS or phone call to verify your account."
                  />

                  <p>
                    <strong>Privacy statement</strong>
                  </p>
                  <p>
                    We are committed to protecting your privacy. The Fair Work
                    Ombudsman manages personal information in accordance with
                    the Privacy Act 1988. When you use My account, we only
                    collect and use information about you that we need to
                    respond to your workplace enquiry or to carry out the
                    functions of the Fair Work Ombudsman.
                  </p>
                  <p>
                    Further information about accessing and correcting your
                    personal information, if you think it is inaccurate, out of
                    date, incomplete, irrelevant or misleading can be found in
                    our Privacy Policy. It also has information about how you
                    can complain about a breach of the Australian Privacy
                    Principles and how we will deal with your complaint.
                  </p>
                  <p className="indent form-field">
                    <span className="hasLongLabel">
                      <label htmlFor="chkTermsAndConditions">
                        By registering for My account you agree to the&nbsp;
                        <a
                          href={ENQUIRIES_CONFIRMATION_PRIVACY_URL}
                          target="_blank"
                          rel="noreferrer"
                        >
                          My account privacy statement
                        </a>
                        &nbsp;and&nbsp;
                        <a
                          href={TERMS_AND_CONDITIONS}
                          target="_blank"
                          rel="noreferrer"
                        >
                          terms and conditions
                        </a>
                        .
                      </label>
                    </span>
                  </p>

                  <p className="form-field">
                    <Button
                      disabled={isSubmitting || !isValid || isSaved}
                      loading={isSubmitting}
                      type="submit"
                      content="Register"
                      className="is-primary"
                    />
                  </p>
                  <Conditional if={isValid && !isSubmitting && isSaved}>
                    <Message positive>
                      <Message.Header>
                        <FontAwesomeIcon icon={faCheckCircle} />
                        &nbsp;You have successfully registered for&nbsp;
                        <a href={PATHS.Home}>My account</a>.
                      </Message.Header>
                    </Message>
                  </Conditional>
                  <FormErrorDisplay
                    show={userErrors?.length > 0}
                    errorsList={userErrors}
                    heading="Please review the following errors"
                  />
                </div>
              </div>
            </Form>
          )}
        </Formik>
      </ErrorBoundary>
    </>
  )
})
export default Register
